// Modal con las clases del día del profesor + alumnos apuntados + acción cancelar.

import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { ApiError } from '@/services/api';
import { cancelBooking, getBookingDetail, type BookingDetailStudent, type BookingListItem } from '@/services/bookings';
import styles from './TeacherDayDialog.module.scss';

interface TeacherDayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  day: string | null;
  bookings: BookingListItem[];
  onChanged: () => void;
}

function formatDayEs(day: string): string {
  const d = new Date(`${day}T00:00:00`);
  const s = d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function TeacherDayDialog({ open, onOpenChange, day, bookings, onChanged }: TeacherDayDialogProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content} aria-describedby={undefined}>
          <header className={styles.header}>
            <Dialog.Title className={styles.title}>{day ? formatDayEs(day) : ''}</Dialog.Title>
            <Dialog.Close asChild>
              <button className={styles.closeBtn} aria-label="Cerrar">
                <Icon name="x" size={20} />
              </button>
            </Dialog.Close>
          </header>
          <div className={styles.body}>
            {bookings.length === 0 ? (
              <p className={styles.muted}>No tienes clases este día.</p>
            ) : (
              <ul className={styles.list}>
                {bookings.map((b) => (
                  <BookingDetailItem key={b.id} booking={b} onChanged={onChanged} />
                ))}
              </ul>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface BookingDetailItemProps {
  booking: BookingListItem;
  onChanged: () => void;
}

function BookingDetailItem({ booking, onChanged }: BookingDetailItemProps) {
  const [students, setStudents] = useState<BookingDetailStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getBookingDetail(booking.id)
      .then((r) => { if (!cancelled) setStudents(r.students); })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Error'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [booking.id]);

  async function doCancel() {
    setCancelling(true);
    setError(null);
    try {
      await cancelBooking(booking.id, 'cancelado por profesor');
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    } finally {
      setCancelling(false);
    }
  }

  const isGroup = booking.type === 'gr';
  const isCancelled = booking.status === 'cancelled';

  return (
    <li className={`${styles.item} ${isCancelled ? styles.cancelled : ''}`}>
      <div className={styles.itemHead}>
        <div className={styles.timeBlock}>
          <Icon name="clock" size={14} />
          <strong>{formatTime(booking.scheduled_at)}</strong>
          <span className={styles.muted}> · {booking.duration_min} min</span>
        </div>
        <Badge tone={isGroup ? 'warning' : 'accent'}>
          {isGroup ? `Grupo · ${booking.active_students}/${booking.max_students}` : 'Individual'}
        </Badge>
        {isCancelled && <Badge tone="danger">Cancelada</Badge>}
      </div>

      <div className={styles.studentsBlock}>
        {loading ? (
          <Spinner size="sm" />
        ) : students.length === 0 ? (
          <p className={styles.muted}>{isGroup ? 'Sin alumnos apuntados' : 'Sin alumno'}</p>
        ) : (
          <ul className={styles.students}>
            {students.map((s) => (
              <li key={s.student_id}>
                <strong>{s.student_display_name ?? `Alumno #${s.student_id}`}</strong>
                {s.student_email && <span className={styles.muted}> · {s.student_email}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {error && <p className={styles.error}>{error}</p>}

      {!isCancelled && (
        <div className={styles.itemActions}>
          <Button variant="ghost" size="sm" onClick={() => void doCancel()} disabled={cancelling}>
            <Icon name="x" size={14} /> {cancelling ? 'Cancelando…' : 'Cancelar clase'}
          </Button>
        </div>
      )}
    </li>
  );
}
